/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useContext, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { useAppSelector } from '../../../reducers';
import { generateUrl } from '../../../routes/util';
import { getCurrentUser, isUserManagerByInitiativeId } from '../../../selectors/user';
import { SurveyType } from '../../../types/survey';
import { BasicAlert } from '../../alert/BasicAlert';
import { Menu } from '../../menu/Menu';
import SimpleTooltip from '../../simple-tooltip';
import { SurveyContext } from '../../survey-container/SurveyContainer';

interface SurveySettingsMenuProps {
  isReadOnly?: boolean;
}

export const SurveySettingsMenu = (props: SurveySettingsMenuProps) => {
  const { isReadOnly } = props;

  const history = useHistory();
  const { surveyData } = useContext(SurveyContext);
  const isCombinedReport = useMemo(() => surveyData.type === SurveyType.Aggregation, [surveyData]);

  const isDisabled = Boolean(surveyData.completedDate);

  const isManager = useAppSelector((state) => {
    if (!state.survey.loaded) {
      return false;
    }
    const currentUser = getCurrentUser(state);
    if (currentUser && state.survey.data.roles?.admin?.includes(currentUser._id)) {
      return true;
    }
    return isUserManagerByInitiativeId(state, state.survey.data.initiativeId);
  });

  const params = useParams<{ surveyId: string, initiativeId: string, page: string }>();
  const surveyId = surveyData._id ?? params.surveyId;
  const initiativeId = surveyData.initiativeId ?? params.initiativeId;
  const page = params.page;

  const isCreate = !surveyId;
  const isToolbarDisabled = Boolean(isCreate || !isManager || isReadOnly);

  const getItem = (props: { code: string, disabled?: boolean, label: string }) => {
    const { code, label, disabled } = props;

    const isActive = (code === page) || (isCreate && code === 'configuration');
    const onClick = isActive ? undefined : () => (
      history.push({
        pathname: generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId, page: code }),
        search: history.location.search,
      })
    );

    const isDisabled = (isToolbarDisabled || disabled);
    const upgradeMessage = 'This feature is not available on your account. Please contact your Account Manager for more information';

    const text = isDisabled ?
      <SimpleTooltip text={upgradeMessage}>{label}</SimpleTooltip>
      : label;

    return {
      label: text,
      disabled: isDisabled,
      active: isActive,
      onClick
    }
  }

  const items = [
    getItem({ code: 'configuration', label: 'Configuration' })
  ];

  if (!isCombinedReport) {
    items.push(getItem({ code: 'scope', label: 'Survey scope' }), getItem({ code: 'delegation', label: 'Delegation' }));
  }

  return (
    <>
      {isDisabled ? <BasicAlert type='warning'>All settings as locked because this survey is marked as Completed.</BasicAlert> : null}
      <Menu items={items} />
      <hr />
    </>
  );
};
